import { ApolloClient, useApolloClient } from "@apollo/client";
import React from "react";
import TagManager from "react-gtm-module";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useSubmitFormForMe } from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import {
  GET_SELECTED_SCHOOLS,
  fromGQL,
} from "src/hooks/useSelectedSchoolsByFormId";
import * as GQL from "src/types/graphql";
import { GET_PARENT_ASSIGNED_FORMS } from "../../formTemplates/graphql/queries";
import { useNextStep } from "../../nextStep/NextStepProvider";
import { SUBMIT_FORM } from "../graphql/mutations";

async function trackFormSubmission(
  client: ApolloClient<any>,
  formId: string
): Promise<void> {
  // Fetch school reference IDs and form key for GTM tracking
  let schoolReferenceIds: string[] = [];
  let formKey: string | undefined;
  let enrollmentPeriodKey: string | undefined;
  try {
    const { data } = await client.query<
      GQL.GetSelectedSchools,
      GQL.GetSelectedSchoolsVariables
    >({
      query: GET_SELECTED_SCHOOLS,
      variables: { formId },
      fetchPolicy: "network-only",
    });

    // Use fromGQL to extract schools from all possible locations
    const schools = fromGQL(data);
    schoolReferenceIds = schools
      .map((school) => school.referenceId)
      .filter((id): id is string => id !== null);

    formKey = data.form_by_pk?.form_template?.key ?? undefined;
    enrollmentPeriodKey =
      data.form_by_pk?.form_template?.enrollment_period?.key ?? undefined;
  } catch (error) {
    console.warn("Error fetching school rankings:", error);
  }

  TagManager.dataLayer({
    dataLayer: {
      event: "form-submitted",
      form_id: formId,
      form_key: formKey,
      form_schools: schoolReferenceIds,
      form_enrollment_period_key: enrollmentPeriodKey,
    },
  });
}

export function useSubmitForm() {
  const flags = useFlags(["form-service-form-status"]);

  const gqlHook = useSubmitFormGQL();
  const restHook = useSubmitFormREST();

  if (flags["form-service-form-status"].enabled) {
    return restHook;
  } else {
    return gqlHook;
  }
}

function useSubmitFormGQL() {
  const client = useApolloClient();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [submitForm] = useRemoteDataMutation<
    GQL.SubmitForm,
    GQL.SubmitFormVariables
  >(SUBMIT_FORM);

  const submitFormFn = React.useCallback(
    async (formId: string) => {
      setSubmitting(true);
      await submitForm({
        variables: {
          form_id: formId,
        },
      });

      await trackFormSubmission(client, formId);

      try {
        await FSR.createRuleExecutionsForMe({
          body: {
            data: {
              targets: [{ type: "form", formId }],
            },
          },
        });
      } catch (error) {
        console.warn("Error executing form rules:", error);
      }
      await client.refetchQueries({ include: [GET_PARENT_ASSIGNED_FORMS] });
      setSubmitting(false);
    },
    [client, submitForm]
  );

  return { isSubmitting, submitForm: submitFormFn };
}

function useSubmitFormREST() {
  const { mutateAsync } = useSubmitFormForMe();
  const client = useApolloClient();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const nextStep = useNextStep();
  const submitFormFn = React.useCallback(
    async (formId: string) => {
      setSubmitting(true);
      const response = await mutateAsync({
        path: {
          formId: formId,
        },
      });

      await trackFormSubmission(client, formId);

      nextStep.process(response.data?.data.assignedForms);

      await client.refetchQueries({ include: [GET_PARENT_ASSIGNED_FORMS] });
      setSubmitting(false);
    },
    [client, mutateAsync, nextStep]
  );

  return { isSubmitting, submitForm: submitFormFn };
}
